import { FC } from 'react';
import React from 'react';
import { useGet } from '../../hooks/useApi';
import { uploadHistoryModel, userApi } from '../../APIs/userApi';

import { useTenant } from '../../contexts/TenantContext';
import { UserName } from '../atomic/text';

import { columnDefiner, Table, TableRow } from '../atomic/table';
import { ColumnDef } from '@tanstack/table-core';
import { Loading } from '../atomic/effects';
import { SizeDisplay } from '../utils/SizeDisplay';
import { comma } from '../../utils/FormUtils';
import { YearMonthSelector } from '../utils/YearMonthSelector';
import { UserSelector } from '../utils/UserSelector';
import { TextField } from '../atomic/forms';
import { DownloadUploadActivityLog } from './DownloadUploadActivityLog';

type ActivityData = uploadHistoryModel & TableRow;
const definer = columnDefiner<ActivityData>();
const columns = [
    {
        header: '日付',
        cell: (d) => {
            const { date } = d.row.original;
            return <>{date}</>;
        },
    },
    {
        header: 'ユーザ',
        cell: (d) => {
            const { userId } = d.row.original;
            const { getTenantUser } = useTenant();
            const user = getTenantUser(userId);
            return <UserName user={user}></UserName>;
        },
    },
    {
        header: 'ファイル種別',
        cell: (d) => {
            const { mimeType } = d.row.original;
            return <>{mimeType}</>;
        },
    },
    {
        header: 'ファイル数',
        cell: (d) => {
            const { count } = d.row.original;
            return <>{comma(count)}</>;
        },
    },

    {
        header: 'サイズ',
        cell: (d) => {
            const { size } = d.row.original;
            return <SizeDisplay bytes={size} />;
        },
    },
] satisfies ColumnDef<ActivityData, any>[];

export type MimeTypeSelectorProp = {
    onMimeTypeChange: any;
    mimeType: string;
};
export const MimeTypeSelector: FC<MimeTypeSelectorProp> = ({
    onMimeTypeChange,
    mimeType,
}) => {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label>ファイルタイプ</label>
            <TextField
                value={mimeType}
                onChange={(e) => {
                    onMimeTypeChange(e.target.value);
                }}
            />
        </div>
    );
};

type Prop = {};
export const UploadActivityLog: FC<Prop> = () => {
    const [year, setYear] = React.useState<string>();
    const [month, setMonth] = React.useState<string>();
    const [user, setUser] = React.useState<string>();
    const [mimeType, setMimeType] = React.useState<string>('');
    const [dateChange, onDateChange] = React.useState(new Date());
    const [activity, setActivity] = React.useState<
        uploadHistoryModel[] | undefined
    >(undefined);

    React.useEffect(() => {
        const _year = dateChange.getFullYear().toString();
        const _month = (dateChange.getMonth() + 1).toString();
        setYear(_year);
        setMonth(_month);
    }, [dateChange]);

    return (
        <div className="app-main log">
            <h2 className="header-name">アップロード履歴</h2>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '1rem',
                }}
            >
                <YearMonthSelector onDateChange={onDateChange} after={2026} />
                <UserSelector onUserChange={setUser} />
                <MimeTypeSelector
                    onMimeTypeChange={setMimeType}
                    mimeType={mimeType}
                />
                <DownloadUploadActivityLog
                    activity={activity}
                    year={year}
                    month={month}
                />
            </div>
            <ActivityTable
                year={year}
                month={month}
                userId={user}
                mimeType={mimeType}
                activity={activity}
                setActivity={setActivity}
            />
        </div>
    );
};

type ActivityTableProp = {
    year: string | undefined;
    month: string | undefined;
    userId: string | undefined;
    mimeType: string;
    activity: uploadHistoryModel[] | undefined;
    setActivity: any;
};
const ActivityTable: FC<ActivityTableProp> = ({
    year,
    month,
    userId,
    mimeType,
    activity,
    setActivity,
}) => {
    const { getTenantUser } = useTenant();
    const options = { getTenantUser };

    const uploadHistory = useGet(
        year && month ? userApi.get.uploadHistory(year, month) : null
    );

    React.useEffect(() => {
        if (uploadHistory.data && uploadHistory.data.data) {
            const _activity = () => {
                if (!uploadHistory.data) {
                    return [];
                }
                return uploadHistory.data.data
                    .filter((data) => {
                        return !userId ? true : data.userId === userId;
                    })
                    .filter((data) => {
                        return !mimeType
                            ? true
                            : data.mimeType.includes(mimeType);
                    });
            };
            setActivity(_activity());
        }
    }, [uploadHistory.data?.data, userId, mimeType]);

    if (activity === undefined) {
        return <Loading />;
    }

    return <Table data={activity} columns={columns} options={options} />;
};
